import { PlatformUtilsService } from '../abstractions/platformUtils.service'
import { StorageService } from '../abstractions/storage.service'

import { ConstantsService } from './constants.service'

export class HtmlStorageService implements StorageService {
  private localStorageKeys = new Set(['appId', 'anonymousAppId', 'rememberedEmail', 'passwordGenerationOptions',
    ConstantsService.disableFaviconKey, 'rememberEmail', 'enableGravatars', 'enableFullWidth',
    ConstantsService.localeKey, ConstantsService.autoConfirmFingerprints,
    ConstantsService.vaultTimeoutKey, ConstantsService.vaultTimeoutActionKey, ConstantsService.ssoCodeVerifierKey,
    ConstantsService.ssoStateKey, 'ssoOrgIdentifier'])
  private localStorageStartsWithKeys = ['twoFactorToken_', ConstantsService.collapsedGroupingsKey + '_']
  private memoryStorageStartsWithKeys = ['ciphers_', 'folders_', 'collections_', 'settings_', 'lastSync_']
  private memoryStorage = new Map<string, string>()

  constructor(private platformUtilsService: PlatformUtilsService) { }

  async init() {
    // LockOption -> VaultTimeout (uses the same legacy string value for backwards compat)
    const vaultTimeout = await this.get<number>(ConstantsService.vaultTimeoutKey)
    if (vaultTimeout == null && !this.platformUtilsService.isDev()) {
      await this.save(ConstantsService.vaultTimeoutKey, 15)
    }

    // Default Action to lock
    const vaultTimeoutAction = await this.get<string>(ConstantsService.vaultTimeoutActionKey)
    if (vaultTimeoutAction == null) {
      await this.save(ConstantsService.vaultTimeoutActionKey, 'lock')
    }
  }

  get<T>(key: string): Promise<T> {
    let json: any = null
    if (this.isLocalStorage(key)) {
      json = self.localStorage.getItem(key)
    } else if (this.isMemoryStorage(key)) {
      json = this.memoryStorage.get(key)
    } else {
      json = self.sessionStorage.getItem(key)
    }
    if (json != null) {
      const obj = JSON.parse(json)
      return Promise.resolve(obj as T)
    }
    return Promise.resolve(null as T)
  }

  save(key: string, obj: any): Promise<any> {
    if (obj == null) {
      return this.remove(key)
    }

    const json = JSON.stringify(obj)
    if (this.isLocalStorage(key)) {
      self.localStorage.setItem(key, json)
    } else if (this.isMemoryStorage(key)) {
      this.memoryStorage.set(key, json)
    } else {
      self.sessionStorage.setItem(key, json)
    }
    return Promise.resolve()
  }

  remove(key: string): Promise<any> {
    if (this.isLocalStorage(key)) {
      self.localStorage.removeItem(key)
    } else if (this.isMemoryStorage(key)) {
      this.memoryStorage.delete(key)
    } else {
      self.sessionStorage.removeItem(key)
    }
    return Promise.resolve()
  }

  private isLocalStorage(key: string): boolean {
    if (this.localStorageKeys.has(key)) {
      return true
    }
    for (const swKey of this.localStorageStartsWithKeys) {
      if (key.startsWith(swKey)) {
        return true
      }
    }
    return false
  }

  private isMemoryStorage(key: string): boolean {
    for (const swKey of this.memoryStorageStartsWithKeys) {
      if (key.startsWith(swKey)) {
        return true
      }
    }
    return false
  }
}
